/**
 * Attachments UI (DESIGN.md §3.5/§3.6 "File chip", "Drop zone"): pick or drop files, check them
 * on the client (type by extension, size, count), upload with progress, download from chips.
 * The server re-checks everything by content.
 */
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MESSAGES,
  ATTACHMENT_TYPES_TEXT,
  type AttachmentDto,
  type AttachmentKind,
  type AttachmentLimits,
  type AttachmentsUploaded,
  attachmentKind,
  attachmentLimitsSchema,
  attachmentsUploadedSchema,
  attachmentUrl,
  checkAttachment,
  DEFAULT_MAX_UPLOAD_MB,
  fmtBytes,
  MAX_FILES_PER_UPLOAD,
} from '@hrms/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type DragEvent, type ReactNode, useCallback, useId, useRef, useState } from 'react'
import type { IconType } from 'react-icons'
import {
  LuFile,
  LuFileImage,
  LuFileSpreadsheet,
  LuFileText,
  LuPaperclip,
  LuPresentation,
  LuUpload,
  LuX,
} from 'react-icons/lu'
import { IconButton } from '../../../components/ui/index.ts'
import { ApiError, apiGet } from '../../../lib/api.ts'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'
import { TICKETS_KEY, ticketKeyOf } from './api.ts'

const FALLBACK_LIMITS: AttachmentLimits = {
  maxFileBytes: DEFAULT_MAX_UPLOAD_MB * 1024 * 1024,
  maxFiles: MAX_FILES_PER_UPLOAD,
}

export function useAttachmentLimits(): AttachmentLimits {
  const { data } = useQuery({
    queryKey: ['attachments', 'limits'],
    queryFn: () => apiGet('/attachments/limits', (d) => attachmentLimitsSchema.parse(d)),
    staleTime: Number.POSITIVE_INFINITY,
  })
  return data ?? FALLBACK_LIMITS
}

const KIND_ICON: Record<AttachmentKind | 'other', { icon: IconType; className: string }> = {
  image: { icon: LuFileImage, className: 'text-violet-fg' },
  pdf: { icon: LuFileText, className: 'text-rose-fg' },
  doc: { icon: LuFileText, className: 'text-blue-fg' },
  sheet: { icon: LuFileSpreadsheet, className: 'text-green-fg' },
  slides: { icon: LuPresentation, className: 'text-orange-fg' },
  text: { icon: LuFileText, className: 'text-slate-fg' },
  other: { icon: LuFile, className: 'text-muted' },
}

const KIND_LABEL: Record<AttachmentKind | 'other', string> = {
  image: 'Image',
  pdf: 'PDF',
  doc: 'Document',
  sheet: 'Spreadsheet',
  slides: 'Slides',
  text: 'Text file',
  other: 'File',
}

export function FileIcon({ name, className }: { name: string; className?: string }) {
  const k = KIND_ICON[attachmentKind(name)]
  const Icon = k.icon
  return <Icon aria-hidden className={cn('size-4 shrink-0', k.className, className)} />
}

const chipClass =
  'inline-flex max-w-full min-w-0 items-center gap-2 rounded-card border border-border bg-surface px-2.5 py-1.5 text-[13px]'

/** A file waiting to be uploaded (Raise modal), with a remove button. */
export function PendingChip({ file, onRemove }: { file: File; onRemove?: () => void }) {
  return (
    <li className={chipClass}>
      <FileIcon name={file.name} />
      <span className="min-w-0 truncate">{file.name}</span>
      <span className="tabular shrink-0 text-faint">{fmtBytes(file.size)}</span>
      {onRemove && (
        <IconButton
          size="sm"
          aria-label={`Remove ${file.name}`}
          className="-my-1 -mr-1.5 size-6 text-sm"
          onClick={onRemove}
          {...hint('Remove')}
        >
          <LuX />
        </IconButton>
      )}
    </li>
  )
}

/** A stored attachment: a download link. */
export function AttachmentChip({ attachment: a }: { attachment: AttachmentDto }) {
  const kind = KIND_LABEL[attachmentKind(a.name)]
  return (
    <li className="min-w-0 max-w-full">
      <a
        href={attachmentUrl(a.id)}
        download={a.name}
        className={cn(
          chipClass,
          'text-text transition-colors hover:border-border-strong hover:bg-surface-2',
        )}
        aria-label={`Download ${a.name}, ${kind}, ${fmtBytes(a.size)}`}
        {...hint(`Download ${a.name}`)}
      >
        <FileIcon name={a.name} />
        <span className="min-w-0 truncate">{a.name}</span>
        <span className="tabular shrink-0 text-faint">{fmtBytes(a.size)}</span>
      </a>
    </li>
  )
}

/** Splits picked files into accepted ones and error messages (type, size, count). */
export function vetFiles(
  picked: File[],
  already: number,
  limits: AttachmentLimits,
): { accepted: File[]; errors: string[] } {
  const accepted: File[] = []
  const errors: string[] = []
  for (const f of picked) {
    const err = checkAttachment(f, limits.maxFileBytes)
    if (err) errors.push(err)
    else if (already + accepted.length >= limits.maxFiles) {
      if (!errors.includes(ATTACHMENT_MESSAGES.tooMany)) errors.push(ATTACHMENT_MESSAGES.tooMany)
    } else accepted.push(f)
  }
  return { accepted, errors }
}

/**
 * Dashed drop label with a hidden multi-file input (DESIGN.md §6 "Drop zone"). Keyboard: the
 * input is focusable (the label shows the focus ring); Enter/Space opens the picker.
 */
export function DropZone({
  onFiles,
  disabled,
  label = 'Add receipts, screenshots or documents',
  compact,
  describedBy,
}: {
  onFiles: (files: File[]) => void
  disabled?: boolean
  label?: string
  compact?: boolean
  describedBy?: string
}) {
  const [over, setOver] = useState(false)
  const id = useId()
  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setOver(false)
    if (disabled) return
    const files = [...e.dataTransfer.files]
    if (files.length) onFiles(files)
  }
  return (
    <label
      htmlFor={id}
      data-tour="attach-drop"
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={cn(
        'flex items-center gap-2 rounded-card border border-dashed text-[13px] transition-colors',
        'focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent',
        compact ? 'px-2.5 py-1.5' : 'p-3',
        disabled
          ? 'cursor-not-allowed border-border text-faint'
          : over
            ? 'cursor-copy border-accent bg-accent-soft text-text'
            : 'cursor-pointer border-border-strong text-muted hover:border-accent hover:text-text',
      )}
    >
      <input
        id={id}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        disabled={disabled}
        aria-describedby={describedBy}
        className="sr-only"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          if (files.length) onFiles(files)
        }}
      />
      {compact ? (
        <LuUpload aria-hidden className="size-4 shrink-0 text-teal-fg" />
      ) : (
        <LuPaperclip aria-hidden className="size-4 shrink-0 text-teal-fg" />
      )}
      <span className="min-w-0">
        {label}
        {!compact && <span className="text-faint"> · or drop files here</span>}
      </span>
    </label>
  )
}

export function attachmentHelp(limits: AttachmentLimits) {
  return `Up to ${limits.maxFiles} files, ${Math.round(limits.maxFileBytes / (1024 * 1024))} MB each: ${ATTACHMENT_TYPES_TEXT}.`
}

/** `POST /api/tickets/:key/attachments` with upload progress (fetch can't report it). */
export function uploadAttachments(
  key: string,
  files: File[],
  onProgress?: (fraction: number) => void,
): Promise<AttachmentsUploaded> {
  return new Promise((resolve, reject) => {
    const form = new FormData()
    for (const f of files) form.append('files', f, f.name)
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `/api/tickets/${encodeURIComponent(key)}/attachments`)
    xhr.setRequestHeader('Accept', 'application/json')
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total)
    }
    xhr.onerror = () =>
      reject(new ApiError(0, 'Upload failed. Check your connection and try again.'))
    xhr.onload = () => {
      let data: unknown = null
      try {
        data = JSON.parse(xhr.responseText)
      } catch {
        // non-JSON error page (e.g. a proxy's 413)
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(attachmentsUploadedSchema.parse(data))
        } catch (err) {
          reject(err)
        }
        return
      }
      const body = (data ?? {}) as { error?: unknown; message?: unknown }
      const message =
        typeof body.message === 'string'
          ? body.message
          : xhr.status === 413
            ? 'Those files are too big to upload.'
            : 'Upload failed. Try again.'
      reject(
        new ApiError(
          xhr.status,
          message,
          typeof body.error === 'string' ? body.error : undefined,
          data,
        ),
      )
    }
    xhr.send(form)
  })
}

/** Upload state + refresh of the ticket and lists afterwards. */
export function useUploader(key: string | null) {
  const qc = useQueryClient()
  const [progress, setProgress] = useState<number | null>(null)
  const upload = useCallback(
    async (files: File[], ticketKey = key) => {
      if (!ticketKey) throw new Error('No ticket to attach to')
      setProgress(0)
      try {
        return await uploadAttachments(ticketKey, files, setProgress)
      } finally {
        setProgress(null)
        void qc.invalidateQueries({ queryKey: ticketKeyOf(ticketKey) })
        void qc.invalidateQueries({ queryKey: TICKETS_KEY })
      }
    },
    [key, qc],
  )
  return { upload, progress, uploading: progress !== null }
}

export function UploadProgress({ progress, count }: { progress: number; count: number }) {
  const pct = Math.round(progress * 100)
  return (
    <div className="mt-2" aria-live="polite">
      <p className="mb-1 text-[12.5px] text-muted">
        Uploading {count === 1 ? '1 file' : `${count} files`}… {pct}%
      </p>
      <div
        role="progressbar"
        aria-label="Upload progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        className="h-1.5 overflow-hidden rounded-full bg-surface-3"
      >
        <div className="h-full bg-accent transition-[width]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export function FileErrors({ errors, id }: { errors: string[]; id?: string }) {
  if (errors.length === 0) return null
  return (
    <ul id={id} className="mt-1.5 flex flex-col gap-0.5 text-[12.5px] text-danger" role="alert">
      {errors.map((e) => (
        <li key={e}>{e}</li>
      ))}
    </ul>
  )
}

/**
 * Drawer section: the ticket's files as download chips, plus "Add files" for the raiser and HR.
 */
export function TicketAttachments({
  ticketKey,
  attachments,
  canAttach,
  onUploaded,
}: {
  ticketKey: string
  attachments: AttachmentDto[]
  canAttach: boolean
  onUploaded: (res: AttachmentsUploaded) => void
}) {
  const limits = useAttachmentLimits()
  const { upload, progress } = useUploader(ticketKey)
  const [errors, setErrors] = useState<string[]>([])
  const [count, setCount] = useState(0)
  const errId = useId()
  const busy = useRef(false)

  const onFiles = async (picked: File[]) => {
    if (busy.current) return
    const { accepted, errors: found } = vetFiles(picked, 0, limits)
    setErrors(found)
    if (accepted.length === 0) return
    busy.current = true
    setCount(accepted.length)
    try {
      onUploaded(await upload(accepted))
    } catch (err) {
      setErrors([err instanceof Error ? err.message : 'Upload failed. Try again.'])
    } finally {
      busy.current = false
    }
  }

  if (attachments.length === 0 && !canAttach) return null
  return (
    <section aria-label="Attachments" className="mt-4" data-tour="ticket-attachments">
      <h3 className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-medium text-muted">
        <LuPaperclip aria-hidden className="size-3.5 text-teal-fg" />
        Attachments{attachments.length > 0 && ` (${attachments.length})`}
      </h3>
      <ul className="flex flex-wrap gap-2">
        {attachments.map((a) => (
          <AttachmentChip key={a.id} attachment={a} />
        ))}
        {canAttach && (
          <li className="min-w-0">
            <span {...hint(attachmentHelp(limits))}>
              <DropZone
                compact
                label="Add files"
                onFiles={(f) => void onFiles(f)}
                disabled={progress !== null}
                describedBy={errors.length ? errId : undefined}
              />
            </span>
          </li>
        )}
      </ul>
      {progress !== null && <UploadProgress progress={progress} count={count} />}
      <FileErrors errors={errors} id={errId} />
    </section>
  )
}

/** Stable React keys for picked files (two files can share a name and size). */
const fileIds = new WeakMap<File, number>()
let nextFileId = 1
function fileKey(f: File): number {
  let id = fileIds.get(f)
  if (id === undefined) {
    id = nextFileId++
    fileIds.set(f, id)
  }
  return id
}

/** Raise modal field: chips for picked files + drop zone + errors. */
export function AttachmentPicker({
  files,
  setFiles,
  disabled,
  children,
}: {
  files: File[]
  setFiles: (next: File[]) => void
  disabled?: boolean
  children?: ReactNode
}) {
  const limits = useAttachmentLimits()
  const [errors, setErrors] = useState<string[]>([])
  const errId = useId()
  const add = (picked: File[]) => {
    const { accepted, errors: found } = vetFiles(picked, files.length, limits)
    setErrors(found)
    if (accepted.length) setFiles([...files, ...accepted])
  }
  return (
    <div>
      {files.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-2" aria-label="Files to attach">
          {files.map((f, i) => (
            <PendingChip
              key={fileKey(f)}
              file={f}
              onRemove={
                disabled
                  ? undefined
                  : () => {
                      setFiles(files.filter((_, j) => j !== i))
                      setErrors([])
                    }
              }
            />
          ))}
        </ul>
      )}
      <DropZone
        onFiles={add}
        disabled={disabled || files.length >= limits.maxFiles}
        describedBy={errors.length ? errId : undefined}
      />
      <p className="mt-1.5 text-[12.5px] text-muted">{attachmentHelp(limits)}</p>
      <FileErrors errors={errors} id={errId} />
      {children}
    </div>
  )
}
